import dayjs from "dayjs"
import type { JobCard } from "~/interface/Job"
import { JobAction } from "~/interface/Job"

const LOCAL_STORAGE_PREFIX = "changeprice-dynamodb"

const stripT = (alias?: string) => (alias || "").replace(/^T/i, "")

const isoWithOffset = (input: string | number | Date | undefined): string => {
  const d = input ? dayjs(input as any) : dayjs()
  return d.format("YYYY-MM-DDTHH:mm:ssZ")
}

const buildPayloads = (job: JobCard, customerId: string): any[] => {
  const items: any[] = Array.isArray((job as any).__items) ? (job as any).__items : []
  const json2: Record<string, number> = (job as any).json2 || {}
  if (items.length === 0 || Object.keys(json2).length === 0) return []

  const employeeName = job.employee?.name || "unknown"
  const aliasNumeric = stripT(job.alias)

  return items.map((it: any, idx: number) => {
    const newUnitPrice = Number(json2[it.uuid] ?? 0)
    const oldUnitPrice = Number(it.price ?? 0)
    const baseTs = job.timestamp ? new Date(job.timestamp as any).getTime() : Date.now()
    const ts = isoWithOffset(baseTs + idx)
    const datePart = dayjs(baseTs + idx).format("YYYY-MM-DD")
    return {
      customerId,
      datetime: `${datePart}#${ts}`,
      timestamp: ts,
      employeeName,
      table: aliasNumeric,
      item: it.name || "",
      qty: Number(it.quantity || 1),
      oldUnitPrice,
      newUnitPrice,
      type: "set_unit_price",
    }
  })
}

const saveLocalStorage = (key: string, payload: any) => {
  if (!process.client) return
  try {
    localStorage.setItem(`${LOCAL_STORAGE_PREFIX}:${key}`, JSON.stringify(payload))
  } catch (error) {
    console.error("changePrice: failed to save local", error)
  }
}

const removeLocalStorage = (key: string) => {
  if (!process.client) return
  try {
    localStorage.removeItem(`${LOCAL_STORAGE_PREFIX}:${key}`)
  } catch (error) {
    console.error("changePrice: failed to remove local", error)
  }
}

const getPending = (): { key: string; payload: any }[] => {
  if (!process.client) return []
  const out: { key: string; payload: any }[] = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(`${LOCAL_STORAGE_PREFIX}:`)) {
        const raw = localStorage.getItem(k)
        if (raw) out.push({ key: k.slice(LOCAL_STORAGE_PREFIX.length + 1), payload: JSON.parse(raw) })
      }
    }
  } catch (error) {
    console.error("changePrice: failed to read pending", error)
  }
  return out
}

const postOne = async (dataservice: ReturnType<typeof useDataservice>, key: string, payload: any) => {
  try {
    await dataservice.trackSetPriceAndDiscount.post(payload)
    removeLocalStorage(key)
  } catch (error) {
    console.error("changePrice: post failed, kept in local storage", error)
  }
}

export const syncPendingChangePrice = async (dataservice: ReturnType<typeof useDataservice>) => {
  const pending = getPending()
  if (!pending.length) return
  const batchSize = 25
  for (let i = 0; i < pending.length; i += batchSize) {
    const batch = pending.slice(i, i + batchSize)
    await Promise.all(batch.map(({ key, payload }) => postOne(dataservice, key, payload)))
  }
}

export default defineNuxtPlugin(({ $pinia }) => {
  if (!process.client) return

  const jobV1Store = usePosJobsStore()
  const dataservice = useDataservice()
  const customerId = dataservice.customerId

  onDataserviceReady(dataservice, () => void syncPendingChangePrice(dataservice))
  window.addEventListener("online", () => {
    if (dataservice.isInited.value) void syncPendingChangePrice(dataservice)
  })

  jobV1Store.$onAction((action) => {
    if (action.name !== "add") return
    const job = action.args[0] as JobCard
    const isSync = action.args[1]
    if (!isSync) return
    if (job?.type !== JobAction.SET_PRICE) return

    action.after(() => {
      const payloads = buildPayloads(job, customerId.value)
      payloads.forEach((p) => {
        const key = p.datetime
        saveLocalStorage(key, p)
        void postOne(dataservice, key, p)
      })
    })
  })
})
